import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { Calendar as BigCalendar, momentLocalizer, View, Views, Components } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';

import moment from 'moment';
import 'moment/locale/fr';

import {
    FaRegCalendarAlt,
    FaMapPin,
    FaUser,
    FaUsers,
    FaCommentDots,
    FaPlus,
    FaCalendarCheck,
    FaLink,
    FaCalendarPlus,
    FaTimes
} from 'react-icons/fa';

import { translatedMessages } from './translations';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/sass/styles.scss';
import './Calendar.scss';
import { useCalendar } from './useCalendar';
import { Modal } from '../Modal';
import { formatEventPeriod } from '../../utils/dates';
import { ToolbarHOC } from './Toolbar';
import { CreateEventModal } from './CreateEventModal/CreateEventModal';
import { AgendaEvent } from 'services/agenda/agenda.types';
import { Button } from 'components/Button/Button';
import { isMobile } from 'react-device-detect';
import { IoArrowRedo, IoPersonAdd } from 'react-icons/io5';
import { BsEnvelopePlusFill } from 'react-icons/bs';

import ICalendarLink from 'react-icalendar-link';
import { FaCheck } from 'react-icons/fa6';

moment.locale('fr');

(moment.fn as any).fromNowOrNow = function (a?: boolean) {
    if (Math.abs(moment().diff(this)) < 1000) {
        return 'Maintenant';
    }
    return this.fromNow(a);
};
const DnDCalendar = withDragAndDrop(BigCalendar<AgendaEvent>);
const localizer = momentLocalizer(moment);

export type CalendarProps = {
    title?: string;
    view?: View;
    hideEvents?: boolean;
    size?: string;
};

const CustomHeader = ({ label }: { label: string }) => {
    // Pour la vue hebdomadaire
    if (label.includes(' - ')) {
        return <div className="custom-header-item-label">{label}</div>;
    }
    if (label.split(' ').length === 1) {
        return <div className="custom-header-item-label">{label}</div>;
    }
    const formattedDayLabel = label.split(' ')[1].split('.')[0];
    const formattedDayNumber = Number(label.split(' ')[0]);
    return (
        <div className="custom-header">
            <div className="custom-header-item-label">{formattedDayLabel}</div>
            <div className="custom-header-item-number">{formattedDayNumber}</div>
        </div>
    );
};

const CustomEvent = ({ event }: { event: AgendaEvent }) => {
    return (
        <div
            className={`custom-event status-${event.status} ${event.linked_to ? 'event-linked' : ''}`}
            style={{ display: 'flex', gap: '5px', height: '100%' }}
        >
            <span>
                {event.linked_to && <FaLink style={{ marginRight: '3px' }} />}
                {event.title}
            </span>
        </div>
    );
};

export const Calendar: React.FunctionComponent<CalendarProps> = ({ title, view, hideEvents, size }) => {
    const {
        events,
        setAllEvents,
        isLoading,
        defaultDate,
        moveOrResizeEvent,
        eventStyleGetter,
        handleSelectSlot,
        createEventContent,
        createEvent,
        currentDate,
        openCreateEventModal,
        handleEditEvent,
        setOpenCreateEventModal,
        eventDetail,
        setEventDetail,
        usersFilter,
        setUsersFilter,
        setCurrentDate,
        usersList,
        user,
        colors,
        datalists,
        eventCreationStep,
        setEventCreationStep,
        isDeleteAgendaEventModalOpen,
        setIsDeleteAgendaEventModalOpen,
        handleDeleteAgendaEvent,
        getEventIcs,
        handleStatusEvent
    } = useCalendar();

    return (
        <>
            {isMobile ? (
                <div className="calendar-floating-button">
                    <Button
                        onClick={() => setOpenCreateEventModal(true)}
                        variant="light"
                        className="calendar-floating-button-button"
                        icon={<FaPlus />}
                        iconBefore
                    />
                </div>
            ) : (
                <></>
            )}
            <div style={{ height: '100%', width: '100%' }} className={`${size}-size ${hideEvents ? 'hide-events' : ''}`}>
                <DnDCalendar
                    className="main-calendar"
                    events={events}
                    defaultDate={defaultDate}
                    defaultView={view ?? Views.WEEK}
                    min={new Date(0, 0, 0, 8, 0)}
                    max={new Date(0, 0, 0, 20, 0)}
                    step={30}
                    localizer={localizer}
                    dayLayoutAlgorithm="no-overlap"
                    draggableAccessor={(event) => event.created_by_id === user?.id && event?.linked_to == null}
                    resizableAccessor={(event) => event.created_by_id === user?.id && event?.linked_to == null}
                    onEventDrop={moveOrResizeEvent}
                    onEventResize={moveOrResizeEvent}
                    eventPropGetter={eventStyleGetter}
                    onSelectEvent={(event) => setEventDetail(event)}
                    onSelectSlot={handleSelectSlot}
                    onView={(view) => {
                        setAllEvents([]);
                        setCurrentDate({ date: currentDate.date, view });
                    }}
                    onNavigate={(date, view, action) => setCurrentDate({ date, view })}
                    views={['month', 'week', 'day']}
                    showAllEvents={true}
                    formats={{
                        monthHeaderFormat: 'MMMM YYYY',
                        weekdayFormat: (date: Date) => {
                            const m = moment(date);
                            if (!m.isValid()) throw new Error('Date invalide');

                            const targetIso = m.isoWeekday();
                            const lastDayOfMonth = m.clone().endOf('month');

                            const lastIso = lastDayOfMonth.isoWeekday();
                            const diff = (lastIso - targetIso + 7) % 7;
                            const lastTarget = lastDayOfMonth.clone().subtract(diff, 'days');

                            if (m.isSame(lastTarget, 'day')) {
                                const firstDayOfNextMonth = m.clone().add(1, 'month').startOf('month');
                                const firstIso = firstDayOfNextMonth.isoWeekday();

                                const diffToTarget = (targetIso - firstIso + 7) % 7;
                                const firstTarget = firstDayOfNextMonth.clone().add(diffToTarget, 'days');

                                const lastDayOfNextMonth = firstDayOfNextMonth.clone().endOf('month');
                                const lastIsoNext = lastDayOfNextMonth.isoWeekday();
                                const diffNext = (lastIsoNext - targetIso + 7) % 7;
                                const lastTargetNext = lastDayOfNextMonth.clone().subtract(diffNext, 'days');

                                if (firstTarget.year() === lastTargetNext.year()) {
                                    if (firstTarget.month() === lastTargetNext.month()) {
                                        return `${firstTarget.format('D')} - ${lastTargetNext.format('D')} ${firstTarget.format('MMMM YYYY')}`;
                                    }
                                    return `${firstTarget.format('D MMM')} - ${lastTargetNext.format('D MMM YYYY')}`;
                                }
                                return `${firstTarget.format('D MMM YYYY')} - ${lastTargetNext.format('D MMM YYYY')}`;
                            }

                            if (m.month() === lastTarget.month()) {
                                return `${m.format('D')} - ${lastTarget.format('D')} ${m.format('MMMM YYYY')}`;
                            }
                            if (m.year() === lastTarget.year()) {
                                return `${m.format('D MMM')} - ${lastTarget.format('D MMM YYYY')}`;
                            }
                            return `${m.format('D')} - ${lastTarget.format('D')} ${m.format('MMMM YYYY')}`;
                        },
                        dayHeaderFormat: 'dddd D MMMM',
                        dayRangeHeaderFormat: ({ start }: { start: Date }) => {
                            const startStr = moment(start).format('MMMM YYYY');
                            return `${startStr}`;
                        },
                        timeGutterFormat: (date: Date) => `${moment(date).format('HH')}h`
                    }}
                    components={{
                        toolbar: ToolbarHOC(usersFilter, setUsersFilter, usersList, colors, setOpenCreateEventModal, isLoading),
                        header: !!(!view || view === Views.WEEK || view === Views.MONTH) ? CustomHeader : undefined,
                        event: CustomEvent,
                        dateCellWrapper: ({ children: { props }, value }) => {
                            return (
                                <>
                                    <div className={`${props.className} ${moment(value).diff(moment(), 'days') < 0 ? '' : ''}`}>
                                        <span style={props.style}></span>
                                    </div>
                                </>
                            );
                        }
                    }}
                    popup
                    resizable
                    selectable
                    messages={translatedMessages}
                />

                <Modal
                    open={!!eventDetail}
                    isValidateDestructive={false}
                    title={eventDetail?.label ?? ''}
                    yesText={eventDetail?.created_by_id === user?.id && eventDetail?.linked_to == null ? 'Modifier' : undefined}
                    onValidate={
                        eventDetail?.created_by_id === user?.id && eventDetail?.linked_to == null
                            ? () => eventDetail && handleEditEvent(eventDetail)
                            : undefined
                    }
                    noText={eventDetail?.created_by_id === user?.id && eventDetail?.linked_to == null ? 'Supprimer' : 'Fermer'}
                    onClose={() =>
                        eventDetail?.created_by_id === user?.id && eventDetail?.linked_to == null
                            ? setIsDeleteAgendaEventModalOpen(true)
                            : setEventDetail(undefined)
                    }
                    setCloseOnClose={false}
                    setClose={() => setEventDetail(undefined)}
                >
                    {eventDetail && (
                        <div className="event-detail-modal">
                            {datalists?.event_types && eventDetail.event_type && (
                                <p className="event-type">{datalists.event_types[eventDetail.event_type]}</p>
                            )}
                            {eventDetail?.linked_to ? (
                                <p className="created-by">Invité par {eventDetail.invited_by_fullname}</p>
                            ) : (
                                <p className="created-by">Évènement créé par {eventDetail.invited_by_fullname}</p>
                            )}

                            {eventDetail?.created_by_id === user?.id && eventDetail?.linked_to != null ? (
                                <div className="ics-download-button">
                                    {eventDetail.status === 'pending' && (
                                        <>
                                            <Button
                                                label="Accepter"
                                                onClick={() => handleStatusEvent(eventDetail, 'accepted')}
                                                variant="light"
                                                icon={<FaCheck />}
                                                iconBefore
                                            />
                                            <Button
                                                label="Refuser"
                                                onClick={() => handleStatusEvent(eventDetail, 'refused')}
                                                variant="light"
                                                icon={<FaTimes />}
                                                iconBefore
                                            />
                                        </>
                                    )}
                                    {eventDetail.status === 'accepted' && (
                                        <>
                                            {/*@ts-ignore*/}
                                            <ICalendarLink event={getEventIcs(eventDetail)} filename="evenement.ics">
                                                <Button label="Ajouter sur Outlook" variant="light" icon={<FaCalendarPlus />} iconBefore />
                                            </ICalendarLink>
                                            <Button
                                                label="Annuler"
                                                onClick={() => handleStatusEvent(eventDetail, 'canceled')}
                                                variant="light"
                                                icon={<FaTimes />}
                                                iconBefore
                                            />
                                        </>
                                    )}
                                </div>
                            ) : (
                                <div className="ics-download-button">
                                    {/*@ts-ignore*/}
                                    <ICalendarLink event={getEventIcs(eventDetail)} filename="evenement.ics">
                                        <Button label="Ajouter sur Outlook" variant="light" icon={<FaCalendarPlus />} iconBefore />
                                    </ICalendarLink>
                                </div>
                            )}

                            <div className="event-detail-modal-info">
                                <FaRegCalendarAlt />
                                <p>
                                    {formatEventPeriod(
                                        eventDetail?.started_at ? new Date(eventDetail.started_at) : new Date(),
                                        eventDetail?.ended_at ? new Date(eventDetail.ended_at) : new Date()
                                    )}
                                </p>
                            </div>
                            {eventDetail.address?.formatted && eventDetail.address?.formatted.length > 0 && (
                                <div className="event-detail-modal-info">
                                    <FaMapPin />
                                    <p>{eventDetail.address?.formatted}</p>
                                </div>
                            )}
                            {eventDetail.person_fullname && (
                                <div className="event-detail-modal-info">
                                    <FaUser />
                                    <ul>
                                        <li>
                                            {!isMobile ? (
                                                <a href={`/#/app/admin/persons/${eventDetail.person_id}`}>{eventDetail.person_fullname}</a>
                                            ) : (
                                                <span>{eventDetail.person_fullname}</span>
                                            )}
                                        </li>
                                    </ul>
                                </div>
                            )}

                            <div className="event-detail-modal-info">
                                <FaUsers />
                                <ul>
                                    <li>
                                        <span className={`assistant-status accepted`}></span> {eventDetail.invited_by_fullname} -{' '}
                                        <i>Organisateur</i>
                                    </li>
                                    {eventDetail.assistants_list?.map((assistant) => (
                                        <li key={`${assistant.value}`}>
                                            <span
                                                className={`assistant-status ${eventDetail.assistants_status?.[assistant.value] ?? 'pending'}`}
                                            ></span>{' '}
                                            {assistant.label} - <i>DAPV</i>
                                        </li>
                                    ))}
                                    {eventDetail.refused_assistants_list?.map((assistant) => (
                                        <li key={`refused-${assistant.value}`}>
                                            <span className={`assistant-status refused`}></span> {assistant.label} - <i>DAPV</i>
                                        </li>
                                    ))}
                                    {eventDetail.person_fullname &&
                                        eventDetail.stakeholders_list?.map((stakeholder) => (
                                            <li key={`${stakeholder.value}`}>
                                                {!isMobile ? (
                                                    <a href={`/#/app/admin/persons/${eventDetail.person_id}/stakeholders`}>
                                                        {stakeholder.label}
                                                    </a>
                                                ) : (
                                                    <span>{stakeholder.label}</span>
                                                )}
                                                {' - '}
                                                <i>Proche</i>
                                            </li>
                                        ))}
                                    {eventDetail.partners_list?.map((partner) => (
                                        <li key={`${partner.value}`}>
                                            {!isMobile ? (
                                                <a href={`/#/app/admin/partners/${partner.value}`}>{partner.label}</a>
                                            ) : (
                                                <span>{partner.label}</span>
                                            )}
                                            {' - '}
                                            <i>Partenaire</i>
                                        </li>
                                    ))}
                                    {eventDetail.emails_list?.map((email) => (
                                        <li key={`${email.value}`}>
                                            <span>{email.label}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {eventDetail.observation && (
                                <div className="event-detail-modal-info">
                                    <FaCommentDots />
                                    <p>{eventDetail.observation}</p>
                                </div>
                            )}
                        </div>
                    )}
                </Modal>

                <Modal
                    open={isDeleteAgendaEventModalOpen}
                    setClose={() => setIsDeleteAgendaEventModalOpen(false)}
                    title="Suppression d'un évènement"
                    yesText="Annuler"
                    onValidate={() => setIsDeleteAgendaEventModalOpen(false)}
                    noText="Supprimer"
                    setCloseOnClose={false}
                    onClose={() => handleDeleteAgendaEvent()}
                >
                    <p>
                        {eventDetail && (
                            <>
                                Voulez-vous vraiment supprimer l'évènement {eventDetail.label} (
                                {formatEventPeriod(eventDetail.started_at, eventDetail.ended_at)}) ?
                            </>
                        )}
                    </p>
                </Modal>

                <CreateEventModal
                    open={openCreateEventModal}
                    createEventContent={createEventContent}
                    onValidate={createEvent}
                    setClose={() => {
                        setOpenCreateEventModal(false);
                        setEventCreationStep(1);
                    }}
                    submitText="Créer l'évènement"
                    title={`Nouvel évènement ${formatEventPeriod(
                        createEventContent?.started_at ? new Date(createEventContent.started_at) : new Date(),
                        createEventContent?.ended_at ? new Date(createEventContent.ended_at) : new Date()
                    )}`}
                    onChange={() => null}
                    usersList={usersList}
                    eventCreationStep={eventCreationStep}
                    setEventCreationStep={setEventCreationStep}
                    onEventCreated={createEvent}
                    moveOrResizeEvent={moveOrResizeEvent}
                ></CreateEventModal>
            </div>
        </>
    );
};
